import React from 'react';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';
import Slider from 'components/commercetools-ui/atoms/slider';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import {
  desktop,
  hugeDesktop,
  largeDesktop,
  mediumDesktop,
  mobile,
  tablet,
} from 'helpers/utils/screensizes';
import { breakpoints } from 'helpers/utils/breakpoints';

import { GenericSectionContent, MediaCard, Cta } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import styles from './index.module.css';

export default function NovaRendering({ content }: { content: GenericSectionContent }) {
  const { cards, title, ctas, additional_css, additional_css_class_names } = content;

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'NovaRendering');

  const [isTabletSize] = useMediaQuery(tablet);

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`bg-[#F3EDE9] pt-24 pb-24 lg:pt-40 lg:pb-40 2xl:pt-60 2xl:pb-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        <div className="pl-24 md:pl-28 lg:pl-48 xl:pl-60 2xl:pl-80 pb-28">
          <div className="flex items-center justify-between pr-4">
            <div>
              <Typography className="pb-32 text-32 font-medium text-[#090909] xl:text-36 2xl:text-[3rem]">
                {title}
              </Typography>
            </div>

            {/* Section-level CTAs render as social media icons in the header. target="_blank" by default for external social links; rel="noopener noreferrer" kept for security. */}
            {ctas && ctas.length > 0 && (
              <div className="mb-15 mr-10 flex justify-center gap-48">
                <ul className="flex flex-row gap-12 self-center 2xl:gap-20">
                  {ctas.map((cta: Cta, i: number) => (
                    <li key={i} className="w-20 2xl:w-24">
                      <Link
                        link={cta?.url}
                        target={cta?.target ?? '_blank'}
                        rel="noopener noreferrer"
                        aria-label={cta?.label ?? cta?.icon?.description}
                      >
                        {cta?.icon?.url && (
                          <Image
                            src={cta?.icon?.url}
                            alt={cta?.icon?.description ?? ''}
                            className="mb-20"
                            height={24}
                            width={24}
                          />
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <Slider
            dots={false}
            allowTouchMove
            arrows={isTabletSize}
            innerArrows
            allowArrowsOnTouchDevice
            solidArrows
            nextButtonStyles={{ top: '100%', left: 50 }}
            prevButtonStyles={{ top: '100%', left: 0 }}
            slidesPerView={1.5}
            spaceBetween={16}
            breakpoints={{
              [mobile]: {
                slidesPerView: 2.75,
                spaceBetween: 16,
                ...(breakpoints[mobile] ?? {}),
              },
              [tablet]: {
                slidesPerView: 2.7,
                spaceBetween: 16,
                ...(breakpoints[tablet] ?? {}),
              },
              [mediumDesktop]: {
                slidesPerView: 5.85,
                spaceBetween: 16,
                ...(breakpoints[mediumDesktop] ?? {}),
              },
              [desktop]: {
                slidesPerView: 3.75,
                spaceBetween: 16,
                ...(breakpoints[desktop] ?? {}),
              },
              [largeDesktop]: {
                slidesPerView: 5.5,
                spaceBetween: 16,
                ...(breakpoints[largeDesktop] ?? {}),
              },
              [hugeDesktop]: {
                slidesPerView: 6.5,
                spaceBetween: 16,
                ...(breakpoints[hugeDesktop] ?? {}),
              },
            }}
          >
            {cards?.map((slide: MediaCard, index: number) => (
              <div key={index} className="mb-48">
                <div className="relative h-[15rem] md:h-[15rem] xl:h-[12rem] 2xl:h-[15rem]">
                  {slide?.desktop_image?.url ? (
                    <Image
                      loading="lazy"
                      src={slide?.desktop_image?.url}
                      alt={slide?.desktop_image?.description ?? ''}
                      fill
                      style={{ objectFit: 'cover' }}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                  ) : (
                    <div className="flex-1"></div>
                  )}
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </div>
    </>
  );
}
